/* tslint:disable:max-file-line-count */

import {
  GalleryViewModel,
  HotelMapModel,
  ImageViewModel,
  IntentMediaModel,
  LocationInfoViewModel,
  LocationSummaryViewModel,
  ParagraphTitleViewModel,
  ParagraphViewModel,
  PrimaryPageMessagingModel,
  RatePlanLoyaltyModel,
  RegionViewModel,
  ShortlistModel
} from 'models';

export interface HotelDetailsViewModel {
  additionalInfo?: PropertyAdditionalInfoViewModel;
  locationInfo?: LocationInfoViewModel;
  amenities?: AllAmenitiesViewModel;
  changeSearchDisplayOptions?: any;
  defaultData?: any;
  error?: any;
  hotelId: number;
  hotelName?: string;
  englishHotelName?: string;
  id: number;
  gallery?: GalleryViewModel;
  mapModel?: HotelMapModel;
  name?: string;
  omnitureData?: string;
  overview?: OverviewViewModel;
  primaryPageMessagingModel?: PrimaryPageMessagingModel;
  propertySummaryDescription?: string;
  region?: RegionViewModel;
  shortlistModel?: ShortlistModel;
  starRatingIcon?: string;
  stars?: string;
  vipMessage?: string;
  teleSalesNumber?: string;
  propertyTypeBadge?: string;
  questionAnswersViewModel?: QuestionAnswersViewModel;
  propertyManagerViewModel?: PropertyManagerViewModel;
  vrInquiryChatbotViewModel?: VrInquiryChatbotViewModel;
  navigationBarTabList?: NavigationBarTabModel[];
  detailsPageTitle?: string;
  propertyFaqs?: PropertyFaqsViewModel;
  intentMedia?: IntentMediaModel;
}

export interface AllAmenitiesViewModel {
  highlightAmenities?: HighlightAmenitiesViewModel[];
  property?: AmenityViewModel[];
  room?: AmenityViewModel[];
  top?: AmenityViewModel[];
  topSummary?: AmenityViewModel[];
}

export interface HighlightAmenitiesViewModel {
  label: string;
  sections?: AmenityViewModel[];
  title?: string;
}

export interface AmenityViewModel {
  additionalInformation?: string;
  category?: string; // enum class AmenityCategory
  description?: string;
  icon?: string;
  mark?: string;
  id?: number;
  subDescription?: string;
  headerText?: string;
}

export interface MarketingSectionViewModel {
  sectionTitle?: string;
  marketingItems?: AmenityViewModel[];
}

export interface AmenityBreakDownViewModel {
  excluded?: AmenityViewModel[];
  included?: AmenityViewModel[];
}

export interface RatePlanAmenityListViewModel {
  amenities?: AmenityViewModel[];
  icon?: string;
  idPrefix?: string;
  ratePlanLoyaltyModel?: RatePlanLoyaltyModel;
  triggerDialogId: string;
  amenitiesSpacing?: string;
  useMiniDialog?: boolean;
}

export interface RatePlanMarketingInfoViewModel extends RatePlanAmenityListViewModel {
  sectionTitle?: string;
}

export interface OverviewViewModel {
  addressLine?: string;
  latitude?: string;
  longitude?: string;
  preview?: string;
  staticMapSrc?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  countryCode?: string;
}

export interface PropertyAdditionalInfoViewModel {
  hrefNextPage: string;
  locationSummary?: LocationSummaryViewModel;
  petsPolicy?: PropertyInfoSubSectionViewModel;
  primaryPropertyMessaging?: PropertyInfoSubSectionViewModel;
  propertyAlternativeName?: PropertyAlternativeNameModel;
  secondaryPropertyMessaging?: PropertySecondaryInfoViewModel;
  shouldMention?: PropertyInfoSubSectionViewModel;
  propertyAmenitiesSection: PropertyInfoSectionViewModel;
  roomAmenitiesSection: PropertyInfoSectionViewModel;
  nearbyThingsSection: PropertyInfoSectionViewModel;
  propertyDescriptionSection?: PropertyInfoSectionViewModel;
  renovationsAndClosuresSection?: PropertyInfoSectionViewModel;
  allInclusiveSection?: PropertyInfoSectionViewModel;
  awardsAndAffiliationsSection?: PropertyInfoSectionViewModel;
  propertyManagerSection?: PropertyInfoSectionViewModel;
  entirePlaceSection?: PropertyInfoSectionViewModel;
  nationalRatingSection?: PropertyInfoSubSectionViewModel;
}

export interface PropertyAlternativeNameModel {
  sections: AlternativeNames[];
  title: string;
}

export interface AlternativeNames {
  name: string;
  value: string;
}

export interface PropertyInfoSectionViewModel {
  sections: PropertyInfoSubSectionViewModel[];
  title: string;
  formattedText?: FormattedViewModel;
  image?: ImageViewModel;
  dataLabel?: string;
  toggleHidden?: ToggleAttributesModel;
}

export interface PropertyInfoSubSectionViewModel {
  body?: string[];
  descriptions?: string[];
  title: string;
  description?: string;
  formattedDescriptions?: FormattedViewModel[];
  formattedDescription?: FormattedViewModel;
  icon?: string;
}

export interface ToggleAttributesModel {
  mobile: boolean;
  desktop: boolean;
  analyticsId: string;
}

export interface FormattedViewModel {
  description: string;
  sectionType?: SectionType;
  textType: TextType;
}

export enum SectionType {
  BULLETED_LIST = 'BULLETED_LIST',
  PLAIN_LIST = 'PLAIN_LIST'
}

export enum TextType {
  HTML_TEXT = 'HTML_TEXT',
  PLAIN_TEXT = 'PLAIN_TEXT'
}

export interface PropertySecondaryInfoViewModel {
  description: string;
  heading: string;
}

export interface PropertyInfoViewModel {
  importantInfo?: PropertyInfoSectionViewModel;
  policies?: PropertyInfoPolicyViewModel;
  priceGuarantee?: PropertyInfoPriceGuaranteeViewModel;
}

export interface PropertyInfoPolicyViewModel {
  info: HotelPolicyViewModel;
  title?: string;
}

export interface PropertyInfoPriceGuaranteeViewModel {
  description?: string;
  url?: string;
  message: string;
}

export interface HotelPolicyViewModel {
  checkinInstructions?: ParagraphViewModel;
  childBeds?: ParagraphViewModel;
  paymentOptions?: PaymentOptionViewModel[];
  houseRules?: ParagraphTitleViewModel;
  checkinPolicies?: ParagraphViewModel;
  checkoutPolicies?: ParagraphViewModel;
  accessMethodPolicies?: ParagraphViewModel;
}

export interface PaymentOptionViewModel {
  imageUrl?: string;
  name: string;
}

export interface VrPropertySummaryViewModel {
  bedroomCountText: string;
  bathroomCountText: string;
  sleepGuestCountText: string;
}

export interface PropertyManagerViewModel {
  title: string;
  name: string;
  languagesSpoken?: string;
  photo?: ImageViewModel;
}

export interface QuestionAnswersViewModel {
  enabledForConventionalLodging?: boolean; // Abacus: 30083_PWA_HIS_QUESTIONS_ANSWERS_TOP_DESTINATIONS
  enabledForHomeAway?: boolean; // Abacus: 30161_PWA_HIS_HOMEAWAY_INQUIRY
}

/**
 * Stores each tab section's name and their localized tab name.
 */
export interface NavigationBarTabModel {
  tabSection: string;
  localizedTabName: string;
}

export interface VrInquiryChatbotViewModel {
  botYouLabel: string;
  cardTitle: string;
  checkInLabel: string;
  checkOutLabel: string;
  closeLabel: string;
  contactOwnerLabel: string;
  contextBackLabel: string;
  dialogTitle: string;
  feedbackContent: string;
  feedbackTitle: string;
  formLabel: string;
  headerLabel: string;
  sendLabel: string;
  twilioTokenUrl: string;
  cardHeader: string;
  cardContent: string;
}

export interface PropertyFaqsViewModel {
  faqs: Faq[];
  title: string;
}

export interface Faq {
  question: string;
  answer: string;
}
